import type { MutationKey } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { showSuccessToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import { useKeywordsCampaignsContext } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContext";
import type {
    IRTCurrentCampaignData,
    IRTRemoveKeywordsOrUpdateTagsOnly,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { Update_Tags_Success_Toast_Key } from "app/common/campaigns/KeywordsCampaginKeywordsTableConsts";
import { RankTrackerQueryType } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { useRankTrackerMutation } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks";
import type { RankTrackerMutationOptions } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks/useRankTrackerMutation";

import { updateKeywordsFromCampaign } from "../KeywordsCamapaignKeywordsReportTableUtils";
import type { IKeywordsReportTableData } from "../KeywordsReportTypes";

export const MUTATION_KEY: MutationKey = ["useUpdateTagsMutation"] as const;

const updateTagsInTableData = (
    currentData: IKeywordsReportTableData | undefined,
    editedTags: Array<{ keyword: string; tags: string[] }>,
): IKeywordsReportTableData | undefined => {
    if (!currentData) {
        return currentData;
    }

    const tagsMap = new Map<string, string[]>();
    editedTags.forEach(({ keyword, tags }) => {
        tagsMap.set(keyword, tags);
    });

    return {
        ...currentData,
        records: currentData.records.map((record) => {
            const updatedTags = tagsMap.get(record.keyword);
            if (updatedTags !== undefined) {
                return {
                    ...record,
                    tags: updatedTags,
                };
            }
            return record;
        }),
    };
};

type UseRankTrackerMutationArgs = RankTrackerMutationOptions<
    IRTCurrentCampaignData,
    Error,
    IRTRemoveKeywordsOrUpdateTagsOnly,
    IKeywordsReportTableData,
    { affectedKeywordsCount: number }
>;

type UseTagsMutationArgs = Pick<UseRankTrackerMutationArgs, "onError" | "onSuccess">;

export const useUpdateTagsMutation = ({ onError, onSuccess }: UseTagsMutationArgs) => {
    const { currentCampaignConfiguration } = useKeywordsCampaignsContext();
    const dispatch = useDispatch();

    return useRankTrackerMutation<
        IRTCurrentCampaignData,
        Error,
        IRTRemoveKeywordsOrUpdateTagsOnly,
        IKeywordsReportTableData,
        { affectedKeywordsCount: number }
    >({
        mutationKey: MUTATION_KEY,
        campaignId: currentCampaignConfiguration?.id,
        queryType: RankTrackerQueryType.KeywordsTable,
        mutationFn: async (campaignConfig: IRTRemoveKeywordsOrUpdateTagsOnly) => {
            if (!currentCampaignConfiguration) {
                throw new Error("No campaign configuration available");
            }
            return updateKeywordsFromCampaign(campaignConfig, currentCampaignConfiguration.id);
        },
        optimisticUpdate: (campaignConfig: IRTRemoveKeywordsOrUpdateTagsOnly) => {
            const editedTags = campaignConfig.updatedTags
                ? Object.entries(campaignConfig.updatedTags).map(([keyword, tags]) => ({
                      keyword,
                      tags,
                  }))
                : [];
            return (cachedData: IKeywordsReportTableData) => updateTagsInTableData(cachedData, editedTags);
        },
        onMutate: (campaignConfig) => {
            const affectedKeywordsCount = campaignConfig.updatedTags
                ? Object.keys(campaignConfig.updatedTags).length
                : 0;
            const toastTextSuccess = translate(
                affectedKeywordsCount === 1
                    ? Update_Tags_Success_Toast_Key
                    : `${Update_Tags_Success_Toast_Key}.multipleItems`,
                {
                    count: affectedKeywordsCount,
                },
            );
            dispatch(showSuccessToast(toastTextSuccess));
            return { affectedKeywordsCount };
        },
        onError,
        onSuccess,
    });
};
